// Restores the legacy `param.data_set = { "<ts_ms>": "<value>.0" }` structure from the
// "Dynamic Multiplexed Columnar" `optimized_session_data` sent by GET /v2/nurse/patients/.

import { mapChannelToParamType } from './channelParamMap';

const CANONICAL_ALIASES = {
  fhr1: 'fhr',
  fhr: 'fhr',
  fhr2: 'fhr_two',
  fhr_two: 'fhr_two',
  toco: 'uc',
  uc: 'uc',
  mhr: 'mhr',
  spo2: 'spo2',
  bp: 'bp',
};

// sync_v / async_params use the backend channel names (FHR1, TOCO...); store them under the param_type names
const canonicalName = (n) => mapChannelToParamType(String(n).trim().toUpperCase());

export function reconstructSessionData(dataset) {
  if (!dataset || !dataset.optimized_session_data) {
    return;
  }

  const opt = dataset.optimized_session_data;
  const reconstructedSets = {};

  // 1. Synchronous parameters (share sync_t)
  if (
    typeof opt.sync_base_time === 'number' &&
    Array.isArray(opt.sync_t) &&
    opt.sync_v &&
    typeof opt.sync_v === 'object'
  ) {
    const base = opt.sync_base_time;
    const tArray = opt.sync_t;

    for (const [paramName, values] of Object.entries(opt.sync_v)) {
      if (!Array.isArray(values)) continue;
      const dict = {};
      const len = Math.min(tArray.length, values.length);
      for (let i = 0; i < len; i++) {
        dict[String(base + tArray[i])] = `${values[i]}.0`;
      }
      reconstructedSets[canonicalName(paramName)] = dict;
    }
  }

  // 2. Asynchronous parameters (bp, dropped-out sensor channels)
  if (opt.async_params && typeof opt.async_params === 'object') {
    for (const [paramName, asyncData] of Object.entries(opt.async_params)) {
      if (
        asyncData &&
        typeof asyncData.base_time === 'number' &&
        Array.isArray(asyncData.t) &&
        Array.isArray(asyncData.v)
      ) {
        const dict = {};
        const len = Math.min(asyncData.t.length, asyncData.v.length);
        for (let i = 0; i < len; i++) {
          dict[String(asyncData.base_time + asyncData.t[i])] = `${asyncData.v[i]}.0`;
        }
        reconstructedSets[canonicalName(paramName)] = dict;
      }
    }
  }

  // 3. Map back onto params by param_type
  if (Array.isArray(dataset.params)) {
    for (const param of dataset.params) {
      if (!param) continue;
      const rawType = (param.param_type || '').trim().toLowerCase();
      const canonical = CANONICAL_ALIASES[rawType] || rawType;

      if (reconstructedSets[canonical]) {
        param.data_set = reconstructedSets[canonical];
      } else if (reconstructedSets[rawType]) {
        param.data_set = reconstructedSets[rawType];
      } else if (!param.data_set) {
        param.data_set = {};
      }
    }
  }

  delete dataset.optimized_session_data;
}

// Reconstructs all sessions in-place. Accepts the legacy `response[]` list as well as `patients[]`.
export function reconstructPatientsPayload(payload) {
  if (!payload) return payload;

  const patientList = Array.isArray(payload)
    ? payload
    : Array.isArray(payload.patients)
    ? payload.patients
    : Array.isArray(payload.response)
    ? payload.response
    : [];

  for (const patient of patientList) {
    if (!patient || !Array.isArray(patient.keyar_data)) continue;
    for (const dataset of patient.keyar_data) {
      reconstructSessionData(dataset);
    }
  }

  return payload;
}
